import { Box } from "@twilio-paste/core/box";
import {
  MessageBlockRole,
  WiseOwlApplicationEnum,
  WiseOwlBlockDisplayType,
  WiseOwlBlockType,
  WiseOwlElementIds,
  WiseOwlIngress,
} from "@twiliointernal/e2-commons-shared-definitions";
import { findLastIndex } from "@twiliointernal/e2-commons-shared-utils";
import { ReactElement } from "react";
import {
  MessageRenderer,
  ButtonGroup,
  CardGroup,
  LinkGroup,
  Combobox,
  LiveChat,
  ToolCallRenderer,
  ToolCallStatusLine,
  LoginCTARenderer,
  TableRenderer,
  DataGridRenderer,
  SupportCtaRenderer,
  PromptSuggestions,
  PromptSuggestionGroup,
  HomeInsights,
  UpsellCTA,
  DescriptionPopoverRenderer,
} from "./sub";
import IntelligentDiscoveryAssistantHome from "../intelligentDiscoveryAssistant/IntelligentDiscoveryHome";
import FeedbackRenderer from "./sub/FeedbackRenderer";
import { useWiseOwlContext } from "../../hooks";
import {
  BlockRendererProps,
  GetRendererForBlockProps,
} from "./definitions/types";

const getRendererForBlock = ({
  applicationId,
  block,
  ingress,
}: GetRendererForBlockProps) => {
  switch (block.type) {
    case WiseOwlBlockType.DescriptionPopover:
      return <DescriptionPopoverRenderer block={block} />;
    case WiseOwlBlockType.Combobox:
      return <Combobox block={block} />;
    case WiseOwlBlockType.ButtonGroup:
      return <ButtonGroup block={block} />;
    case WiseOwlBlockType.CardGroup:
      return <CardGroup block={block} />;
    case WiseOwlBlockType.DataGrid:
      return <DataGridRenderer block={block} />;
    case WiseOwlBlockType.Feedback:
      return <FeedbackRenderer block={block} />;
    case WiseOwlBlockType.IntelligentDiscoveryHome:
      return <IntelligentDiscoveryAssistantHome block={block} />;
    case WiseOwlBlockType.LinkGroup:
      return <LinkGroup block={block} />;
    case WiseOwlBlockType.LiveChat:
      return <LiveChat block={block} />;
    case WiseOwlBlockType.LoginCTA:
      return <LoginCTARenderer block={block} />;
    case WiseOwlBlockType.Message:
      return <MessageRenderer block={block} />;
    case WiseOwlBlockType.PromptSuggestionGroup:
      // Tricky: Some Console pages currently require the old pre-chat experience
      if (
        ingress === WiseOwlIngress.console &&
        applicationId !== WiseOwlApplicationEnum.voiceInsights
      ) {
        return <PromptSuggestionGroup block={block} />;
      }

      return <PromptSuggestions block={block} />;
    case WiseOwlBlockType.SupportCta:
      return <SupportCtaRenderer block={block} />;
    case WiseOwlBlockType.ToolCall:
      return <ToolCallRenderer block={block} />;
    case WiseOwlBlockType.ToolCallStatusLine:
      return <ToolCallStatusLine block={block} />;
    case WiseOwlBlockType.TableBlock:
      return <TableRenderer block={block} />;
    case WiseOwlBlockType.HomeInsights:
      return <HomeInsights block={block} />;
    case WiseOwlBlockType.UpsellCTA:
      return <UpsellCTA block={block} />;
    default:
      return <></>;
  }
};

const BlockRenderer = ({ blocks }: BlockRendererProps): ReactElement => {
  const {
    applicationProps: { applicationId, ingress },
  } = useWiseOwlContext();

  const latestUserMessage = findLastIndex(
    blocks,
    (block) =>
      block.type === WiseOwlBlockType.Message &&
      block.values.role === MessageBlockRole.User
  );

  return (
    <Box>
      {blocks.map((block, index) => {
        /**
         * Don't render transient blocks if they are before the latest user message
         */
        if (
          block.display === WiseOwlBlockDisplayType.Transient &&
          index < latestUserMessage
        ) {
          return <></>;
        }

        return (
          <Box
            key={`${block.type}-${index}`}
            element={WiseOwlElementIds.WISE_OWL_BLOCK_RENDERER_CONTAINER}
          >
            <Box
              element={WiseOwlElementIds.WISE_OWL_BLOCK_RENDERER}
              marginBottom={
                block.type === WiseOwlBlockType.IntelligentDiscoveryHome
                  ? "space0"
                  : "space30"
              }
            >
              {getRendererForBlock({
                applicationId,
                block,
                ingress,
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default BlockRenderer;
